import { CLINIC_IDENTITY } from './clinicIdentity';
import type { ExportPatientData, ExportSessionData, ResolvedProfessional } from './types';
import { getCicloTerapeuticoLabel, getCicloVitalEtapaLabel, getMotivoCategoriaLabel } from '../clinicalRecords/constants';

// Mismo criterio que ClinicalRecords.tsx (parseLocalDate) — "1990-05-15" es
// DATE sin hora, pero `new Date(iso)` lo interpreta como UTC medianoche y
// corre un día hacia atrás en horario de Chile. Duplicado a propósito (5
// líneas, sin dependencia cruzada UI↔lib) — si se corrige ahí, corregir acá.
function parseLocalDate(iso: string): Date {
  const [year, month, day] = iso.slice(0, 10).split('-').map(Number);
  return new Date(year, month - 1, day);
}

export function formatDate(iso?: string): string {
  if (!iso) return '—';
  return parseLocalDate(iso).toLocaleDateString('es-CL', { day: 'numeric', month: 'long', year: 'numeric' });
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  return `${d.toLocaleDateString('es-CL', { day: 'numeric', month: 'long', year: 'numeric' })}, ${d.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' })}`;
}

function labelPrevision(prev?: string): string {
  if (!prev) return '—';
  const map: Record<string, string> = { isapre: 'Isapre', fonasa: 'Fonasa', particular: 'Particular' };
  return map[prev] ?? prev;
}

function labelModalidad(modalidad: string): string {
  return modalidad === 'online' ? 'Telemática (videollamada)' : 'Presencial';
}

function labelTipoConsulta(tipo: string): string {
  const map: Record<string, string> = { individual: 'Individual', pareja: 'Pareja', familiar: 'Familiar' };
  return map[tipo] ?? tipo;
}

function professionalFullLabel(p: ResolvedProfessional): string {
  // `terapeutas.titulo` NO es un título profesional limpio en los datos reales
  // (verificado en prod: para los 2 terapeutas existentes contiene el mismo
  // texto que especialidades.join(', ') — es un campo legacy/duplicado, no un
  // título tipo "Psicóloga Clínica"). No se usa acá para no repetir la lista
  // completa de especialidades en cada bloque de firma del PDF.
  const nombre = `${p.nombre} ${p.apellido}`.trim();
  const partes: string[] = [];
  if (p.rut) partes.push(`RUT ${p.rut}`);
  if (p.registroProfesional) partes.push(`Reg. profesional ${p.registroProfesional}`);
  if (p.numeroLicencia) partes.push(`Licencia ${p.numeroLicencia}`);
  return partes.length > 0 ? `${nombre} — ${partes.join(' · ')}` : nombre;
}

// ─── Header / Footer (se repiten en cada página) ────────────────────────────

export function buildLetterheadHeader(logoDataUri: string | null) {
  return (currentPage: number) => ({
    margin: [40, 20, 40, 0] as [number, number, number, number],
    columns: [
      logoDataUri
        ? { image: logoDataUri, width: 48, margin: [0, 0, 10, 0] as [number, number, number, number] }
        : { text: '', width: 48 },
      {
        stack:
          currentPage === 1
            ? [
                { text: CLINIC_IDENTITY.razonSocial, bold: true, fontSize: 12 },
                { text: `RUT ${CLINIC_IDENTITY.rut}`, fontSize: 8, color: '#555555' },
                { text: CLINIC_IDENTITY.direccion, fontSize: 8, color: '#555555' },
                { text: `${CLINIC_IDENTITY.telefono} · ${CLINIC_IDENTITY.email}`, fontSize: 8, color: '#555555' },
              ]
            : [{ text: CLINIC_IDENTITY.razonSocial, fontSize: 9, color: '#555555' }],
        width: '*',
      },
    ],
  });
}

export function buildFooter(generadoPor: { nombre: string; apellido: string }) {
  const generadoEl = new Date().toLocaleString('es-CL', { dateStyle: 'medium', timeStyle: 'short' });
  return (currentPage: number, pageCount: number) => ({
    margin: [40, 0, 40, 20] as [number, number, number, number],
    stack: [
      { text: 'Documento confidencial — Ley 20.584. Contiene datos sensibles de salud.', fontSize: 7, color: '#888888' },
      {
        columns: [
          { text: `Generado por ${generadoPor.nombre} ${generadoPor.apellido} el ${generadoEl}`, fontSize: 7, color: '#888888' },
          { text: `Página ${currentPage} de ${pageCount}`, fontSize: 7, color: '#888888', alignment: 'right' as const },
        ],
      },
    ],
  });
}

// Marca de agua institucional — logo centrado y semitransparente detrás del
// contenido de cada página. Usa `background` (no `watermark`, que en pdfmake
// es solo texto diagonal tipo "BORRADOR" y no sirve para un logo), que
// acepta una función (currentPage, pageSize) => contenido, igual que
// header/footer. Confirmado que pdfmake@0.3.11 (versión instalada) soporta
// `opacity` en nodos `image` dentro de `background` — verificado leyendo el
// código fuente de la librería, no es una apuesta.
export function buildWatermarkBackground(logoDataUri: string | null) {
  if (!logoDataUri) return undefined;
  return (_currentPage: number, pageSize: { width: number; height: number }) => ({
    image: logoDataUri,
    width: 220,
    opacity: 0.08,
    absolutePosition: { x: (pageSize.width - 220) / 2, y: 60 },
  });
}

// ─── Bloques de identificación (Ley 20.584 Art. 12) ─────────────────────────

export function buildPatientIdentificationBlock(paciente: ExportPatientData) {
  return {
    stack: [
      { text: 'Identificación del paciente', style: 'sectionTitle' },
      {
        columns: [
          { text: [{ text: 'Nombre: ', bold: true }, `${paciente.nombre} ${paciente.apellido}`] },
          { text: [{ text: 'RUT: ', bold: true }, paciente.rut] },
        ],
      },
      {
        columns: [
          { text: [{ text: 'Fecha de nacimiento: ', bold: true }, formatDate(paciente.fecha_nacimiento)] },
          { text: [{ text: 'Previsión: ', bold: true }, labelPrevision(paciente.prevision)] },
        ],
      },
    ],
    margin: [0, 0, 0, 10] as [number, number, number, number],
  };
}

export function buildProfessionalIdentificationBlock(profesional: ResolvedProfessional, label = 'Profesional tratante') {
  return {
    stack: [
      { text: label, style: 'sectionTitle' },
      { text: professionalFullLabel(profesional) },
    ],
    margin: [0, 0, 0, 10] as [number, number, number, number],
  };
}

// ─── Contenido clínico de una sesión (formato reducido) ────────────────────
//
// Formato reducido (2026-09): el editor de ficha pasó a "Encabezado clínico"
// (3 catálogos) + "Registro clínico de la sesión" = 4 observaciones. El PDF
// calca eso. Las columnas de la plantilla v3 anterior (antecedentes,
// intervenciones, plan, evaluación de continuidad, etc.) siguen en `consultas`
// pero ya no se editan ni se exportan. Mismo criterio de mapeo honesto:
// 'Sin registro.' como fallback en campos vacíos, nunca se inventa contenido.
//
// Mapeo observación → columna: I=observacion_clinica, II=desarrollo_sesion,
// III=respuesta_evolucion, IV=observaciones_adicionales.

function formatCurrency(valor?: number): string {
  return valor != null ? `$${valor.toLocaleString('es-CL')}` : '—';
}

export function buildClinicalSessionBlock(sesion: ExportSessionData, profesional?: ResolvedProfessional) {
  const block: any[] = [
    {
      stack: [
        {
          columns: [
            { text: [{ text: 'Fecha: ', bold: true }, formatDateTime(sesion.fecha_hora)] },
            { text: [{ text: 'Duración: ', bold: true }, `${sesion.duracion} min`] },
          ],
        },
        {
          columns: [
            { text: [{ text: 'Tipo de consulta: ', bold: true }, labelTipoConsulta(sesion.tipo_consulta)] },
            { text: [{ text: 'Modalidad: ', bold: true }, labelModalidad(sesion.modalidad)] },
          ],
        },
        {
          columns: [
            { text: [{ text: 'Costo: ', bold: true }, formatCurrency(sesion.valor_final)] },
            { text: [{ text: 'Ciclo terapéutico: ', bold: true }, getCicloTerapeuticoLabel(sesion.ciclo_terapeutico)] },
          ],
        },
        {
          columns: [
            { text: [{ text: 'Ciclo vital: ', bold: true }, getCicloVitalEtapaLabel(sesion.ciclo_vital_etapa)] },
            { text: '' },
          ],
        },
      ],
      margin: [0, 0, 0, 6],
    },
  ];

  if (profesional) {
    block.push({ text: [{ text: 'Atendida por: ', bold: true }, professionalFullLabel(profesional)], margin: [0, 0, 0, 6] });
  }

  block.push(
    { text: 'Motivo / contexto de la sesión', style: 'fieldLabel' },
    {
      text: [
        { text: 'Categoría: ', bold: true },
        getMotivoCategoriaLabel(sesion.motivo_categoria),
        '\n',
        { text: 'Específico: ', bold: true },
        sesion.motivo_consulta?.trim() || 'Sin registro.',
      ],
      margin: [0, 0, 0, 6],
    },

    { text: 'Registro clínico de la sesión', style: 'sectionTitle' },
    { text: 'Observación I', style: 'fieldLabel' },
    { text: sesion.observacion_clinica?.trim() || 'Sin registro.', margin: [0, 0, 0, 6] },
    { text: 'Observación II', style: 'fieldLabel' },
    { text: sesion.desarrollo_sesion?.trim() || 'Sin registro.', margin: [0, 0, 0, 6] },
    { text: 'Observación III', style: 'fieldLabel' },
    { text: sesion.respuesta_evolucion?.trim() || 'Sin registro.', margin: [0, 0, 0, 6] },
    { text: 'Observación IV', style: 'fieldLabel' },
    { text: sesion.observaciones_adicionales?.trim() || 'Sin registro.', margin: [0, 0, 0, 6] }
  );

  return { stack: block };
}

// ─── Cierre de sesión: timbre profesional y control de ficha ───────────────
// Van DENTRO del `content` al cierre de cada sesión — NO son el `footer` de
// página (que sigue siendo el pie chico de confidencialidad Ley 20.584, sin
// tocar acá).

export function buildProfessionalStampBlock(profesional: ResolvedProfessional) {
  const nombreCompleto = `${profesional.nombre} ${profesional.apellido}`.trim();
  // `terapeutas.titulo` es dato sucio en prod hoy (ver nota en
  // professionalFullLabel más arriba: para los 2 terapeutas reales contiene
  // la lista de especialidades, no un título limpio tipo "Psicóloga
  // Clínica"). Se usa igual si viene poblado, por instrucción explícita —
  // 'Psicólogo/a' es solo el fallback cuando no hay nada cargado.
  const tituloLinea = profesional.titulo?.trim() || 'Psicólogo/a';

  const lineas: any[] = [
    {
      canvas: [{ type: 'line', x1: 197, y1: 0, x2: 317, y2: 0, lineWidth: 0.5, lineColor: '#AAAAAA' }],
      margin: [0, 14, 0, 6] as [number, number, number, number],
    },
    { text: nombreCompleto, alignment: 'center', bold: true, fontSize: 9 },
    { text: tituloLinea, alignment: 'center', fontSize: 8, color: '#555555' },
  ];
  if (profesional.rut) {
    lineas.push({ text: `RUT ${profesional.rut}`, alignment: 'center', fontSize: 8, color: '#555555' });
  }
  if (profesional.registroProfesional) {
    lineas.push({ text: `N° de registro ${profesional.registroProfesional}`, alignment: 'center', fontSize: 8, color: '#555555' });
  }

  return { stack: lineas, margin: [0, 10, 0, 4] as [number, number, number, number] };
}

export function buildFichaControlBlock(
  sesion: ExportSessionData,
  sessionNumber: number | undefined,
  profesional: ResolvedProfessional
) {
  return {
    stack: [
      { text: 'Control de ficha', style: 'sectionTitle' },
      {
        columns: [
          { text: [{ text: 'Fecha de atención: ', bold: true }, formatDate(sesion.fecha_hora)] },
          { text: [{ text: 'Ciclo terapéutico: ', bold: true }, getCicloTerapeuticoLabel(sesion.ciclo_terapeutico)] },
        ],
      },
      {
        columns: [
          { text: [{ text: 'Sesión N°: ', bold: true }, sessionNumber != null ? String(sessionNumber) : '—'] },
          { text: [{ text: 'Modalidad: ', bold: true }, labelModalidad(sesion.modalidad)] },
        ],
      },
      { text: [{ text: 'Profesional responsable: ', bold: true }, professionalFullLabel(profesional)] },
    ],
    margin: [0, 6, 0, 4] as [number, number, number, number],
  };
}

export const DOC_STYLES = {
  title: { fontSize: 16, bold: true, margin: [0, 0, 0, 10] as [number, number, number, number] },
  sectionTitle: { fontSize: 10, bold: true, color: '#1E4A8F', margin: [0, 4, 0, 4] as [number, number, number, number] },
  fieldLabel: { fontSize: 9, bold: true, color: '#555555', margin: [0, 4, 0, 2] as [number, number, number, number] },
};
